import { useCallback, useMemo, useState, useEffect } from 'react';

import { useSavedViews } from './use-saved-views';

import { Filter } from '@/types/filters';

export function usePullsFilters() {
  const { savedViews, loadView, touchView, isLoading } = useSavedViews();
  
  // Find the most recently used saved view
  const mostRecentView = useMemo(() => {
    if (!savedViews || savedViews.length === 0) return null;
    
    // Sort by updatedAt descending to get most recent
    return [...savedViews].sort((a, b) => 
      new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    )[0];
  }, [savedViews]);
  
  // Initialize filters from most recent view or empty
  const [filters, setFilters] = useState<Filter[]>([]);
  const [currentViewId, setCurrentViewId] = useState<string>('');
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Load the most recent view on mount
  useEffect(() => {
    // Only initialize after Zero has finished loading AND we have either loaded a view or confirmed there are none
    if (!isInitialized && !isLoading && mostRecentView) {
      const loadedFilters = loadView(mostRecentView.id);
      
      if (loadedFilters) {
        setFilters(loadedFilters);
        setCurrentViewId(mostRecentView.id);
        // Touch the view to maintain its "most recent" status
        touchView(mostRecentView.id);
      }
      setIsInitialized(true);
    }
  }, [isInitialized, isLoading, mostRecentView, loadView, touchView]);
  
  // Enhanced setFilters that tracks when filters are modified
  const setFiltersWithTracking = useCallback((newFilters: Filter[] | ((prev: Filter[]) => Filter[])) => {
    setFilters(newFilters);
    // Note: We don't clear currentViewId here because the user might still be working with this view
    // They need to explicitly save or switch views
  }, []);
  
  // Method to load a specific view
  const loadViewWithTouch = useCallback((viewId: string, directFilters?: Filter[]) => {
    if (viewId) {
      // If directFilters are provided (e.g., from a newly created view), use them
      // Otherwise, try to load from saved views
      const filtersToUse = directFilters || loadView(viewId);
      
      if (filtersToUse) {
        setFilters(filtersToUse);
        setCurrentViewId(viewId);
        touchView(viewId);
      }
    } else {
      // Loading "All pulls" - clear filters
      setFilters([]);
      setCurrentViewId('');
    }
  }, [loadView, touchView]);
  
  return {
    filters,
    setFilters: setFiltersWithTracking,
    currentViewId,
    loadView: loadViewWithTouch,
  };
}